/** Authoritative RO diagnosis → commercial review → PIC send → customer approval workflow. */

import { hasRoleCapability } from "./userRoles";
import { projectRepairOrderCommercialFields, type CommercialViewer } from "./roWorkTimer";

export const RO_REPAIR_PROPOSAL_STATUSES = [
  "awaiting_commercial_review",
  "ready_to_send_to_customer",
] as const;

export const GENERIC_STATUS_BYPASS_BLOCKED = [
  "awaiting_commercial_review",
  "ready_to_send_to_customer",
  "awaiting_customer_confirmation",
  "in_progress",
  "customer_declined",
] as const;

export const TERMINAL_RO_STATUSES = ["closed", "customer_declined"] as const;

export const COMMERCIAL_ROLES = new Set(["admin", "finance"]);

export const TECHNICIAN_HIDDEN_COMMERCIAL_FIELDS = [
  "customerCharge",
  "actualCost",
  "sellingPrice",
  "margin",
] as const;

export const SHOW_EMPTY_SEND_LABEL = "SEND PROPOSAL TO CLIENT";
export const REPAIR_APPROVED_BANNER = "REPAIR APPROVED — PLEASE PROCEED";
export const APPROVE_REPAIR_LABEL = "APPROVE REPAIR";
export const DECLINE_REPAIR_LABEL = "DECLINE REPAIR";

/** Staff-facing process (same sequence as customer operations). */
export const CLARITY_REPAIR_ORDER_WORKFLOW_STEPS = [
  {
    title: "1. Technician completes the diagnosis",
    body: "Record the diagnosed fault, recommended repair, suggested repair amount, and estimated duration. Saving sends the repair to Admin/Finance for commercial review. Customer contact belongs to the PIC. Starting repair before approval requires the separate audited technician exception.",
  },
  {
    title: "2. Admin/Finance confirms the selling price",
    body: "Approve the technician’s suggestion or enter the final amount to charge the customer. The proposal then goes to the assigned PIC. This does not start the repair.",
  },
  {
    title: "3. PIC records approval or sends the proposal",
    body: "Review the customer-facing diagnosis, proposed repair, and selling price. Either APPROVE ON BEHALF OF CUSTOMER and record the approval basis, or SEND PROPOSAL TO CLIENT for the customer to decide. The assigned technician may separately start without approval with a recorded reason; this does not record customer consent.",
  },
  {
    title: "4. Customer reviews the proposal",
    body: "The customer uses a secure link (no OTP) to APPROVE REPAIR or DECLINE REPAIR.",
  },
  {
    title: "5. Approval authorizes the repair",
    body: "Whether recorded by the PIC on behalf of the customer or through the customer link, the Portal records approval, sets the repair to Repair Approved / In Progress, emails and notifies the PIC and assigned technician, and shows REPAIR APPROVED — PLEASE PROCEED. The technician may then begin the repair.",
  },
  {
    title: "6. Customer declines",
    body: "The repair moves to Customer Declined. The PIC is notified to follow up. The technician is not instructed to proceed.",
  },
] as const;

export function isCommercialRole(role: string | null | undefined): boolean {
  return COMMERCIAL_ROLES.has((role ?? "").toLowerCase());
}

export function canTechnicianSeeSellingPrice(role: string | null | undefined): boolean {
  return hasRoleCapability({ role }, ["admin", "finance", "sales"]);
}

/**
 * Role-and-ownership projection for repair-order commercial fields.
 * The historical name is kept for existing callers. Passing only a role is
 * not enough to reveal the suggested internal cost: that requires the
 * assigned technician, Admin, or Finance.
 */
export function stripCommercialFieldsForTechnician<T extends Record<string, unknown>>(
  job: T,
  role: string | null | undefined,
  viewer?: Omit<CommercialViewer, "role">,
): T {
  return projectRepairOrderCommercialFields(job, { role, ...viewer });
}

export function diagnosisNextStatus(partsReferToFinance: boolean): string {
  return partsReferToFinance ? "pending_finance" : "awaiting_commercial_review";
}

export function genericStatusChangeBlocked(
  currentStatus: string,
  nextStatus: string,
): string | null {
  if (currentStatus === nextStatus) return null;
  if (TERMINAL_RO_STATUSES.includes(currentStatus as (typeof TERMINAL_RO_STATUSES)[number])) {
    return "This repair order is closed or declined and cannot change via generic status update.";
  }
  if (currentStatus === "in_progress" && nextStatus === "pending_parts") return null;
  if (currentStatus === "pending_parts" && nextStatus === "in_progress") return null;
  if (
    (currentStatus === "in_progress" || currentStatus === "pending_parts") &&
    ["repair_complete", "awaiting_payment", "awaiting_collection", "closed"].includes(nextStatus)
  ) {
    return null;
  }
  if (GENERIC_STATUS_BYPASS_BLOCKED.includes(nextStatus as (typeof GENERIC_STATUS_BYPASS_BLOCKED)[number])) {
    return "This status can only be reached through commercial review, PIC send, or customer approval.";
  }
  if (GENERIC_STATUS_BYPASS_BLOCKED.includes(currentStatus as (typeof GENERIC_STATUS_BYPASS_BLOCKED)[number])) {
    if (nextStatus === "closed" || nextStatus === "repair_complete") {
      return "Use the authorised no-charge or repair-complete actions rather than a generic status change.";
    }
  }
  return null;
}

/**
 * Statuses that the generic editor may offer for a given current status.
 * Lifecycle-controlled transitions remain available only through their
 * authoritative workflow actions (commercial review, PIC send, or approval).
 */
export function genericStatusUpdateOptions(
  currentStatus: string,
  statuses: readonly string[],
): string[] {
  return statuses.filter((nextStatus) => genericStatusChangeBlocked(currentStatus, nextStatus) === null);
}

export type SendGateInput = {
  status: string;
  sellingPrice: string | number | null | undefined;
  snapshotCurrent: boolean;
  snapshotSuperseded: boolean;
  customerEmail: string | null | undefined;
  picEmail: string | null | undefined;
  picActive: boolean;
  technicianEmail: string | null | undefined;
  technicianActive: boolean;
  alreadySent?: boolean;
};

export function assertPicSendReady(input: SendGateInput): string | null {
  if (input.status !== "ready_to_send_to_customer") {
    return "Proposal can only be sent when the repair order is ready to send to the customer.";
  }
  if (input.sellingPrice == null || String(input.sellingPrice).trim() === "") {
    return "A confirmed customer selling price is required.";
  }
  if (input.snapshotSuperseded) return "This proposal version has been superseded. Confirm the updated proposal first.";
  if (!input.snapshotCurrent) return "A current customer-facing proposal snapshot is required.";
  if (!String(input.customerEmail ?? "").trim()) return "A customer email is required.";
  if (!input.picActive || !String(input.picEmail ?? "").trim()) {
    return "An active assigned PIC with email is required.";
  }
  if (!input.technicianActive || !String(input.technicianEmail ?? "").trim()) {
    return "An active assigned technician with email is required.";
  }
  return null;
}

export function idempotencyKey(kind: string, jobId: number, version: number): string {
  return `ro:${kind}:${jobId}:v${version}`;
}

export function approvalEmailContainsPricing(html: string): boolean {
  return /S\$|selling price|customer charge|internal cost|quoted cost|technician suggestion/i.test(html);
}

export function buildApprovalProceedEmailHtml(params: {
  jobNumber: string;
  customerName: string;
  productLabel: string;
  portalUrl: string;
  recipientName: string;
}): string {
  return `
    <p>Dear ${escapeBasic(params.recipientName)},</p>
    <p>The customer has approved the repair. Work may proceed.</p>
    <div class="job-number">${escapeBasic(params.jobNumber)}</div>
    <table style="width:100%; border-collapse:collapse; padding-top:16px;">
      <tr><td style="padding:8px 0; font-weight:bold; color:#555; width:160px;">Customer</td><td>${escapeBasic(params.customerName)}</td></tr>
      <tr><td style="padding:8px 0; font-weight:bold; color:#555;">Product</td><td>${escapeBasic(params.productLabel)}</td></tr>
    </table>
    <p style="padding-top:24px;">Please open the Portal to continue.</p>
    <p style="padding-top:16px;"><a href="${escapeBasic(params.portalUrl)}">Open repair order</a></p>
  `;
}

export function customerSafeProposalFields(input: {
  jobNumber: string;
  brand: string;
  model: string;
  customerFacingDiagnosis: string | null;
  proposedRepair: string | null;
  sellingPrice: string | null;
  estimatedDuration: string | null;
}): Record<string, string | null> {
  return {
    jobNumber: input.jobNumber,
    product: `${input.brand} ${input.model}`.trim(),
    diagnosedFault: input.customerFacingDiagnosis,
    proposedRepair: input.proposedRepair,
    quotedCost: input.sellingPrice,
    estimatedDuration: input.estimatedDuration,
  };
}

function escapeBasic(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
